/* ==========================================================================
   Ship-to picker, and putting the buyer in their own market.

   Shopify already knows the buyer's country and already prices every market;
   what the shop was missing was anything that acts on it. Both paths here post
   to Shopify's own /localization endpoint — nothing rewrites money in the
   browser, because the cart and the checkout read the market server-side and a
   price swapped only in the DOM would be a lie by the time anyone paid.
   ========================================================================== */
(function () {
  'use strict';

  var AUTO_KEY = 'hm:market-auto';
  var DISMISS_KEY = 'hm:market-rec-dismissed';
  var DISMISS_MS = 30 * 24 * 60 * 60 * 1000;

  /* Deliberately no bare "preview": bingpreview is named in full, and the loose
     token would match real browsers and quietly drop them out of the automatic
     path. A false positive here costs a buyer their currency. */
  var BOTS = /bot|crawl|spider|slurp|bingpreview|lighthouse|headless|pagespeed|gtmetrix/i;

  /* --- ship-to select ---------------------------------------------------
     Delegated, so a selector re-rendered by the theme editor keeps working. */
  document.addEventListener('change', function (event) {
    var select = event.target.closest ? event.target.closest('[data-ship-to]') : null;
    if (!select || !select.form) return;
    select.form.submit();
  });

  /* The submit button is the no-JS path. Hide it only once this script runs,
     so the form is never a dead end. */
  function hideFallbacks() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-ship-to-go]'),
      function (btn) { btn.hidden = true; }
    );
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hideFallbacks);
  } else {
    hideFallbacks();
  }

  /* --- market resolution ------------------------------------------------- */
  class MarketRecommendation extends HTMLElement {
    connectedCallback() {
      var data = this.querySelector('[data-market-rec-countries]');
      if (!data) return;
      try {
        this.countries = JSON.parse(data.textContent);
      } catch (e) {
        return;
      }

      var close = this.querySelector('[data-market-rec-dismiss]');
      if (close) close.addEventListener('click', this.dismiss.bind(this));

      this.auto = this.dataset.auto === 'true';
      this.prompt = this.dataset.prompt === 'true';
      this.exclude = (this.dataset.autoExclude || '').toUpperCase().split(',')
        .map(function (s) { return s.trim(); })
        .filter(Boolean);

      this.resolve();
    }

    /* The head already started this request; reuse it rather than paying for a
       second round trip. */
    geo() {
      if (window.HM && window.HM.geo) return window.HM.geo;
      return fetch(this.dataset.suggestUrl, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }

    resolve() {
      var self = this;
      this.geo().then(function (payload) {
        var detected = payload && payload.detected_values && payload.detected_values.country;
        var code = detected && detected.handle;
        if (!code || code === self.dataset.current) return;

        // A country outside every market has nowhere to go, and the primary
        // market is already the right answer for it.
        var country = self.countries[code];
        if (!country) return;

        if (self.canAutoSwitch(code)) {
          self.apply(code);
        } else if (self.prompt && !self.isDismissed()) {
          self.show(code, country);
        }
      });
    }

    canAutoSwitch(code) {
      if (!this.auto) return false;

      // Markets that live on another URL. Switching those is a navigation, not
      // a currency change, so they get asked instead of moved.
      if (this.exclude.indexOf(code) > -1) return false;

      // Reloading the page under the merchant mid-edit is unforgivable.
      if (window.Shopify && window.Shopify.designMode) return false;

      // Crawlers stay in the primary market, so the canonical URL keeps
      // indexing one currency instead of whichever datacentre asked.
      if (BOTS.test(navigator.userAgent)) return false;

      // One attempt per session. If Shopify declines the switch — an
      // unsupported country, a cookie that will not stick — a second attempt
      // would reload the page forever.
      try {
        if (sessionStorage.getItem(AUTO_KEY)) return false;
        sessionStorage.setItem(AUTO_KEY, code);
      } catch (e) {
        return false;   // no storage, no loop guard, no auto-switch
      }
      return true;
    }

    apply(code) {
      var field = this.querySelector('[data-market-rec-code]');
      if (!field || !field.form) return;
      field.value = code;
      field.form.submit();
    }

    isDismissed() {
      try {
        return Number(localStorage.getItem(DISMISS_KEY) || 0) > Date.now();
      } catch (e) {
        return false;   // private mode: the prompt reappears next visit
      }
    }

    dismiss() {
      try {
        localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS));
      } catch (e) { /* hiding it for this page load is enough */ }
      this.hidden = true;
    }

    show(code, country) {
      var text = this.querySelector('[data-market-rec-text]');
      var go = this.querySelector('[data-market-rec-go]');
      var field = this.querySelector('[data-market-rec-code]');
      if (!text || !go || !field) return;

      text.textContent = 'It looks like you are in ' + country.name +
        '. Shop in ' + country.currency + ' and see delivery for your country.';
      go.textContent = 'Shop in ' + country.name;
      field.value = code;
      this.hidden = false;
    }
  }
  customElements.define('market-recommendation', MarketRecommendation);
})();
